import * as React from "react";

import type { SlotPagingBounds, SlotWindow } from "@/api/types";

/**
 * Month-at-a-time slot loading, shared by the ad-hoc and rolling invitee pages.
 *
 * <p>Both pages ask the same thing of the same shape of response, and both used to take every slot
 * the link had in one payload — which is what handed the backend a range wide enough for Google to
 * reject, and left the guard off. Fetching a month at a time keeps every free/busy query small.</p>
 *
 * <h2>Months are kept, not replaced</h2>
 *
 * <p>Loaded months accumulate. Paging back to somewhere already seen is instant and silent — no
 * request, no spinner, no flash of an empty grid — which matters because an invitee comparing two
 * dates crosses the same boundary repeatedly. The backend caches by month for the same reason, so a
 * month that does leave here usually costs nothing there either.</p>
 *
 * <h2>Bounds are obeyed, never shown</h2>
 *
 * <p>`range_*` is what the link will answer for; asking outside it is a 400 by design, so this never
 * asks. `availability_*` is the narrower span that actually has times, and is what {@link canGoNext}
 * and {@link canGoPrev} stop on — the arrows run out on the last month with something in it rather
 * than walking the invitee through empty months to reach the range end.</p>
 *
 * <p>Neither bound is ever rendered. The invitee finds no further months, indistinguishable from a
 * host with nothing more free. Saying why would name the host's plan.</p>
 */

/** A month as `YYYY-MM`, which is also the cache key. */
export type MonthKey = string;

export function monthKeyOf(d: Date): MonthKey {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

/**
 * The window to request for one month, clamped to the link's range.
 *
 * <p>Clamping rather than sending the bare month is what keeps the 400 unreachable from the UI: the
 * first and last months of a range almost always overhang it, since a horizon of N days rarely lands
 * on a month boundary.</p>
 */
export function windowForMonth(month: Date, bounds: SlotPagingBounds): SlotWindow {
  const from = startOfMonth(month);
  const to = addMonths(month, 1);
  const rangeFrom = bounds.range_from ? new Date(bounds.range_from) : null;
  const rangeTo = bounds.range_to ? new Date(bounds.range_to) : null;
  const clampedFrom = rangeFrom && rangeFrom > from ? rangeFrom : from;
  const clampedTo = rangeTo && rangeTo < to ? rangeTo : to;
  return { from: clampedFrom.toISOString(), to: clampedTo.toISOString() };
}

/**
 * Whether the calendar may step to the month after {@code month}.
 *
 * <p>Falls back to true when the backend sent no bounds (an older deployment), which is the
 * pre-paging behaviour: unbounded arrows. Explicitly null `availability_to` is different from
 * absent — it means the link has nothing to offer at all, so there is nowhere to step.
 */
export function canGoNext(month: Date, bounds: SlotPagingBounds): boolean {
  if (bounds.availability_to === undefined) return true;
  if (!bounds.availability_to) return false;
  const last = new Date(bounds.availability_to);
  return addMonths(month, 1) <= startOfMonth(last);
}

export function canGoPrev(month: Date, bounds: SlotPagingBounds): boolean {
  if (bounds.availability_from === undefined) return true;
  if (!bounds.availability_from) return false;
  const first = new Date(bounds.availability_from);
  return addMonths(month, -1) >= startOfMonth(first);
}

/** The month the calendar should open on: where availability starts, not today. */
export function initialMonth(bounds: SlotPagingBounds): Date {
  const first = bounds.availability_from ? new Date(bounds.availability_from) : new Date();
  return startOfMonth(first);
}

type State<S> = {
  /** Every slot loaded so far, across every month visited. */
  slots: S[];
  /** True only while the month on screen is in flight — a revisit never sets it. */
  loading: boolean;
  /** The month currently shown. */
  month: Date;
};

/**
 * @param seed       the first response's slots and the month they belong to, or null while it is
 *                   still in flight. Consumed once: the page cannot hand this over at mount, since
 *                   the bounds it depends on arrive with the very response being seeded.
 * @param fetchMonth resolves the slots for one window. Errors are swallowed to an empty month: a
 *                   failed page must not replace a working booking page with an error screen, and
 *                   the invitee can still use every month already loaded.
 */
export function useSlotPaging<S>(
  bounds: SlotPagingBounds,
  seed: { slots: S[]; month: Date } | null,
  fetchMonth: (window: SlotWindow) => Promise<S[]>,
): State<S> & { goToMonth: (month: Date) => void } {
  const [byMonth, setByMonth] = React.useState<Map<MonthKey, S[]>>(() => new Map());
  const [month, setMonth] = React.useState<Date>(() => seed?.month ?? new Date());
  const [loading, setLoading] = React.useState(false);

  // Which months have been asked for, successes and failures alike. A month that came back empty is
  // still loaded; re-requesting it on every visit would make an empty month cost a round trip each
  // time the invitee passes through it.
  const requested = React.useRef(new Set<MonthKey>());
  // The month on screen right now, readable from inside a settled promise. An invitee can page past
  // a slow month, and its late arrival must not clear the spinner on the one they are waiting for.
  const shown = React.useRef<MonthKey>("");

  // Adopt the first response once it lands. Guarded by a ref rather than a dependency list because
  // `seed` is a fresh object every render; re-seeding later would throw away every month the invitee
  // has paged to and snap them back to the start.
  const seeded = React.useRef(false);
  React.useEffect(() => {
    if (seeded.current || !seed) {
      return;
    }
    seeded.current = true;
    const key = monthKeyOf(seed.month);
    requested.current.add(key);
    shown.current = key;
    setByMonth(new Map([[key, seed.slots]]));
    setMonth(seed.month);
  }, [seed]);

  const goToMonth = React.useCallback(
    (next: Date) => {
      const key = monthKeyOf(next);
      shown.current = key;
      setMonth(next);
      if (requested.current.has(key)) {
        setLoading(false);
        return;
      }
      requested.current.add(key);
      setLoading(true);
      fetchMonth(windowForMonth(next, bounds))
        .then((slots) => setByMonth((prev) => new Map(prev).set(key, slots)))
        .catch(() => setByMonth((prev) => new Map(prev).set(key, [])))
        .finally(() => {
          if (shown.current === key) {
            setLoading(false);
          }
        });
    },
    [bounds, fetchMonth],
  );

  const slots = React.useMemo(() => [...byMonth.values()].flat(), [byMonth]);

  return { slots, loading, month, goToMonth };
}
